import * as React from "react";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import moment from "moment-timezone";
import _uniq from "lodash/uniq";
import _capitalize from "lodash/capitalize";
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Button, DropDown, RotateLoader, SelectAdv, Tooltip as CwTooltip } from "@lib/components";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { logger, RestaurantUtils, Untrusive } from "@lib/common";
import { MobxComponent } from "../../../../../mobx/components";
import { getOptionalOverviewReportEmail } from "../../../../utils/overview_report_email";
import { UI } from "../../../../../core/ui";

type Range = "day" | "week" | "month" | "financial-year" | "last-financial-year";
type Period = "day" | "week" | "month" | "year";
type DetailMode = "percent" | "amount";
type CountMode = "item" | "category" | "tax";

const ASSET_ROOT = "/images/dashboard-v2";

const Page = styled.div`
	background: #f1f4f7;
	color: #181d27;
	font-family: Inter, sans-serif;
	margin: -2rem;
	min-height: calc(100vh - 64px);
	padding: 24px;

	@media (max-width: 576px) {
		margin: -1rem;
		padding: 16px;
	}
`;

const Header = styled.div`
	align-items: center;
	display: flex;
	gap: 24px;
	justify-content: space-between;
	margin-bottom: 12px;

	@media (max-width: 1100px) {
		align-items: flex-start;
		flex-direction: column;
	}
`;

const HeaderText = styled.div`
	flex: 1;

	h1 {
		font-size: 16px;
		font-weight: 600;
		line-height: 24px;
		margin: 0;
	}

	p {
		color: #535862;
		font-size: 12px;
		line-height: 18px;
		margin: 2px 0 0;
	}
`;

const HeaderActions = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	justify-content: flex-end;

	@media (max-width: 768px) {
		justify-content: flex-start;
		width: 100%;
	}
`;

const Tabs = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
`;

const Tab = styled.button<{ active?: boolean }>`
	appearance: none;
	background: transparent;
	border: 0;
	border-radius: 6px;
	color: ${({ active }) => (active ? "#fb2f2f" : "#414651")};
	cursor: pointer;
	font-family: inherit;
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;
	padding: 8px 12px;
	white-space: nowrap;

	&:hover {
		color: #fb2f2f;
	}
`;

const EmailButton = styled.button`
	align-items: center;
	appearance: none;
	background: #fff;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	color: #414651;
	cursor: pointer;
	display: flex;
	font-family: inherit;
	font-size: 14px;
	font-weight: 500;
	gap: 4px;
	justify-content: center;
	line-height: 20px;
	padding: 6px 12px;

	&:disabled {
		cursor: wait;
		opacity: 0.6;
	}
`;

const EmailPanel = styled.div`
	padding: 12px;
	text-align: left;

	p {
		color: #535862;
		font-size: 13px;
		line-height: 20px;
		margin: 8px 0;
	}

	strong {
		color: #414651;
	}
`;

const Icon = styled.img<{ compact?: boolean; size?: number }>`
	display: block;
	height: ${({ compact, size }) => (size ? `${size}px` : compact ? "16px" : "20px")};
	width: ${({ compact, size }) => (size ? `${size}px` : compact ? "16px" : "20px")};
`;

const Metrics = styled.div`
	display: grid;
	gap: 12px;
	grid-template-columns: repeat(6, minmax(0, 1fr));
	margin-bottom: 12px;

	@media (max-width: 1200px) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	@media (max-width: 768px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 420px) {
		grid-template-columns: 1fr;
	}
`;

const Card = styled.section`
	background: #fff;
	border: 1px solid #e9eaeb;
	border-radius: 12px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
`;

const MetricCard = styled(Card)`
	align-items: center;
	display: flex;
	gap: 12px;
	padding: 20px;
`;

const IconBox = styled.div<{ small?: boolean }>`
	align-items: center;
	background: #f0f4f6;
	border-radius: 8px;
	display: flex;
	flex: 0 0 auto;
	height: 32px;
	justify-content: center;
	width: 32px;
`;

const MetricText = styled.div`
	min-width: 0;

	p {
		color: #535862;
		font-size: 12px;
		line-height: 18px;
		margin: 0 0 2px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	strong {
		color: #181d27;
		display: block;
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;

const ChartCard = styled(Card)`
	margin-bottom: 12px;
	padding: 20px;
`;

const CardTop = styled.div`
	align-items: center;
	display: flex;
	gap: 16px;
	justify-content: space-between;
	margin-bottom: 4px;

	@media (max-width: 650px) {
		align-items: flex-start;
		flex-direction: column;
	}
`;

const ChartSummary = styled.div`
	flex: 1;
	min-width: 0;
	padding-left: 8px;
	strong {
		display: block;
		font-size: 16px;
		font-weight: 600;
		line-height: 24px;
	}

	p {
		color: #6b7270;
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
		margin: 0;
	}
`;

const ChartArea = styled.div`
	height: 344px;
	margin-top: 8px;
	min-width: 0;
	width: 100%;

	.chart-total-label {
		fill: #6b7270;
		font-size: 12px;
	}
`;

const ChartLegend = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	justify-content: center;
	margin-top: 16px;
	width: 100%;
`;

const ChartLegendItem = styled.div`
	align-items: center;
	background: #fff;
	display: flex;
	gap: 10px;
	padding: 12px;
`;

const ChartLegendSwatch = styled.div<{ color: string }>`
	background: ${({ color }) => color};
	border-radius: 4px;
	flex: 0 0 24px;
	height: 24px;
	overflow: hidden;
	width: 24px;
`;

const ChartLegendLabel = styled.p`
	color: #535862;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	margin: 0;
	white-space: nowrap;

	span {
		color: #1c1c1c;
	}
`;

const formatCompact = (value: number) => {
	if (!value) return "0";
	if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}m`;
	if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
	return String(Math.round(value * 100) / 100);
};

const SERVICE_LEGEND: Array<{
	key: T.Schema.Restaurant.Services.RestaurantServiceTypes;
	label: string;
	color: string;
}> = [
	{ key: "delivery", label: "Delivery", color: "#4a0000" },
	{ key: "pickup", label: "Pickup", color: "#a30800" },
	{ key: "dine_in", label: "Dine-in", color: "#ff6262" },
	{ key: "table_booking", label: "Booking", color: "#ffc3cc" },
];

const DetailGrid = styled.div`
	display: grid;
	gap: 12px;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	margin-bottom: 12px;

	@media (max-width: 1400px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const DetailCard = styled(Card)`
	min-height: 360px;
	padding: 20px;
`;

const DetailHeading = styled.div`
	align-items: center;
	display: flex;
	gap: 16px;
	margin-bottom: 16px;

	> div:nth-child(2) {
		flex: 1;
		min-width: 0;
	}

	h2 {
		color: #535862;
		font-size: 12px;
		font-weight: 600;
		line-height: 18px;
		margin: 0;
	}

	p {
		color: #535862;
		font-size: 10px;
		font-weight: 400;
		line-height: 16px;
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;

const CompactTabs = styled(Tabs)`
	flex-wrap: nowrap;

	${Tab} {
		padding: 8px 12px;
	}

	/* hide on mobile and on 4-column layout (≥1400px); show only on 2-column range */
	display: none;

	@media (min-width: 769px) and (max-width: 1399px) {
		display: flex;
	}
`;

const ModeDropdown = styled.select`
	appearance: none;
	background: transparent url("${ASSET_ROOT}/chevron-down.svg") no-repeat right 6px center;
	background-size: 16px 16px;
	border: 0;
	border-radius: 8px;
	color: #414651;
	cursor: pointer;
	/* shown on small screens and 4-column layout (≥1400px) */
	display: block;
	font-family: inherit;
	font-size: 11px;
	font-weight: 500;
	line-height: 18px;
	padding: 4px 26px 4px 6px;

	/* 2-column layout: cards wide enough for tabs — hide dropdown */
	@media (min-width: 769px) and (max-width: 1399px) {
		display: none;
	}
`;

const ProgressItem = styled.div`
	display: flex;
	gap: 12px;
	margin-bottom: 12px;
`;

const RowMarker = styled.div`
	align-items: center;
	display: flex;
	flex: 0 0 24px;
	justify-content: center;

	img {
		display: block;
		height: 15px;
		width: auto;
	}
`;

const ProgressContent = styled.div`
	flex: 1;
	min-width: 0;
`;

const ProgressLabel = styled.div`
	color: #414651;
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;
	margin-bottom: 2px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ProgressLine = styled.div`
	align-items: center;
	display: flex;
	gap: 12px;
`;

const Track = styled.div`
	background: #e9eaeb;
	border-radius: 999px;
	flex: 1;
	height: 8px;
	overflow: hidden;
`;

const Fill = styled.div<{ width: number }>`
	background: #fb2f2f;
	border-radius: inherit;
	height: 100%;
	max-width: 100%;
	width: ${({ width }) => width}%;
`;

const ProgressValue = styled.span`
	color: #414651;
	font-size: 12px;
	font-weight: 500;
	min-width: 40px;
	text-align: right;
`;

const CountCard = styled(Card)`
	padding: 20px;
`;

const CountTitle = styled.h2`
	color: #0c202a;
	flex: 1;
	font-size: 16px;
	font-weight: 700;
	line-height: 32px;
	margin: 0;
`;

const Table = styled.div`
	color: #4b4f5e;
	font-size: 12px;
	line-height: 24px;
`;

const TableRow = styled.div<{ heading?: boolean }>`
	border-bottom: ${({ heading }) => (heading ? "0" : "1px dashed #dfdfdf")};
	color: ${({ heading }) => (heading ? "#4b4f5e" : "#4b4f5e")};
	display: grid;
	font-size: ${({ heading }) => (heading ? "14px" : "12px")};
	font-weight: ${({ heading }) => (heading ? 700 : 400)};
	gap: 8px;
	grid-template-columns: minmax(160px, 1fr) minmax(80px, 1fr);
	padding: ${({ heading }) => (heading ? "0 0 8px" : "4px 0")};

	span:last-child {
		font-weight: 500;
		text-align: right;
	}
`;

const Empty = styled.div`
	color: #6b7270;
	padding: 40px 0;
	text-align: center;
`;

const ErrorPanel = styled.div`
	color: #bc362f;
	padding: 60px 20px;
	text-align: center;

	button {
		background: #fb2f2f;
		border: 0;
		border-radius: 8px;
		color: #fff;
		cursor: pointer;
		margin-top: 16px;
		padding: 10px 18px;
	}
`;

interface Props extends WithTranslation {}

interface State {
	start: number;
	end: number;
	period: Period;
	range: Range;
	chart: "sales" | "orders";
	chartType: "line" | "bar";
	counts: CountMode;
	detailModes: { [key: string]: DetailMode };
	error: boolean;
	data: T.API.DashboardReportsBasicResponse | null;
	emails: string[];
	emailOptions: string[];
	sendEmailActive: boolean;
	isSendingEmail: boolean;
}

interface DetailItem {
	name: string;
	value: number;
	amount: number;
	icon: string;
}

interface DetailDefinition {
	key: string;
	title: string;
	subtitle: string;
	icon: string;
	total: number;
	items: DetailItem[];
}

@inject("store")
@observer
class RestaurantDashboardV2Class extends MobxComponent<Props, State> {
	mounted = false;

	constructor(props: Props) {
		super(props);
		const restaurant = this.injected.store.restaurant!;
		const emailStore = restaurant.settings.notifications.email.store || undefined;
		const optionalEmails = (emailStore && getOptionalOverviewReportEmail(emailStore)) || [];
		const overviewEmails = (emailStore && emailStore.overview_report_emails) || [];
		const emails = overviewEmails.length > 0 ? overviewEmails : optionalEmails;

		this.state = {
			...this.getRange("day"),
			range: "day",
			chart: "sales",
			chartType: "bar",
			counts: "item",
			detailModes: {},
			error: false,
			data: null,
			emails,
			emailOptions: _uniq([...emails, ...optionalEmails]),
			sendEmailActive: false,
			isSendingEmail: false,
		};
	}

	componentDidMount() {
		this.mounted = true;
		this.query();
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	getRange = (range: Range) => {
		const restaurant = this.injected.store.restaurant!;
		const timezone = restaurant.settings.region.timezone;
		const now = moment.tz(Date.now(), timezone);
		let start = now.clone();
		let end = now.clone();
		let period: Period = "day";

		if (range === "week") {
			period = "week";
			start =
				restaurant.settings.region.day_of_the_week === "mon"
					? now.clone().startOf("isoWeek")
					: now.clone().startOf("week");
			end =
				restaurant.settings.region.day_of_the_week === "mon"
					? now.clone().endOf("isoWeek")
					: now.clone().endOf("week");
		} else if (range === "month") {
			period = "month";
			start = now.clone().startOf("month");
			end = now.clone().endOf("month");
		} else if (range === "financial-year" || range === "last-financial-year") {
			period = "year";
			const financialYearStart = now.month() >= 6 ? now.year() : now.year() - 1;
			const offset = range === "last-financial-year" ? -1 : 0;
			start = moment.tz({ year: financialYearStart + offset, month: 6, date: 1 }, timezone).startOf("day");
			end = start.clone().add(1, "year").subtract(1, "millisecond");
		} else {
			start = now.clone().startOf("day");
			end = now.clone().endOf("day");
		}

		return { start: start.valueOf(), end: end.valueOf(), period };
	};

	setRange = (range: Range) => {
		this.setState({ ...this.getRange(range), range, data: null }, this.query);
	};

	query = async () => {
		try {
			this.setState({ error: false });
			const restaurant = this.injected.store.restaurant!;
			const { start, end, period } = this.state;
			const data = await this.injected.store.api.reports_basic({
				restaurant_id: restaurant._id,
				start,
				end,
				period,
			});

			if (this.mounted) {
				this.setState({ data: data.outcome ? null : data, error: !!data.outcome });
			}
		} catch (error) {
			logger.captureException(error);
			if (this.mounted) this.setState({ error: true, data: null });
		}
	};

	sendEmail = async () => {
		try {
			Untrusive.start();
			this.setState({ isSendingEmail: true });
			const restaurant = this.injected.store.restaurant!;
			const { start, end, period, emails } = this.state;
			await this.injected.store.api.send_overview_report_email({
				restaurant_id: restaurant._id,
				start,
				end,
				period,
				emails,
				template: "v2",
			});
			UI.notification.success("Overview Report has been sent!");
			if (this.mounted) this.setState({ sendEmailActive: false });
		} catch (error) {
			logger.captureException(error);
			UI.notification.error("An error occurred, try again soon or contact us.");
		} finally {
			Untrusive.stop();
			if (this.mounted) this.setState({ isSendingEmail: false });
		}
	};

	formatCurrency = (value: number) => {
		const { t, store } = this.injected;
		const symbol = store.restaurant!.settings.region.currency.symbol;
		return symbol ? `${symbol}${value.toFixed(2)}` : t("currency", { value });
	};

	formatRangeTitle = () => {
		const { start, end, range } = this.state;
		const timezone = this.injected.store.restaurant!.settings.region.timezone;
		if (range === "financial-year" || range === "last-financial-year") {
			return `Financial Year ${moment.tz(start, timezone).format("YYYY")}-${moment
				.tz(end, timezone)
				.format("YYYY")}`;
		}
		return `${moment.tz(start, timezone).format("D MMM YYYY")} - ${moment
			.tz(end, timezone)
			.format("D MMM YYYY")}`;
	};

	renderEmailSelect = () => {
		const { emails, emailOptions, isSendingEmail } = this.state;

		if (emailOptions.length === 0) {
			return (
				<EmailPanel>
					<p>No Email Address was found!</p>
					<p>Please visit there and enter up to 4 emails:</p>
					<p>
						<strong>Settings &gt; System &gt; E-Mail Notifications &gt; Overview Report E-Mail</strong>
					</p>
				</EmailPanel>
			);
		}

		return (
			<EmailPanel>
				<SelectAdv
					type="multi"
					value={emails}
					onChange={(options: string[]) => this.setState({ emails: options })}
					options={emailOptions.map(address => ({ label: address, value: address }))}
				/>

				<p>
					Pick up to 4 e-mail addresses to send Overview Report.
					<CwTooltip
						width={450}
						position="bottom"
						offset={10}
						text={
							<div>
								<p>
									You can select from Notification Reply-To Email and Notification Email Addresses
									(if any).
								</p>
								<p>If you also want to use other emails, please visit:</p>
								<p>
									<strong>
										Settings &gt; System &gt; E-Mail Notifications &gt; Overview Report E-Mail
									</strong>
								</p>
								<p>and enter up to 4 emails.</p>
							</div>
						}
					>
						<AiOutlineInfoCircle className="m-l-1" />
					</CwTooltip>
				</p>

				<Button
					full={true}
					color="primary-inverse"
					disabled={isSendingEmail || emails.length === 0}
					onClick={this.sendEmail}
				>
					{isSendingEmail && <RotateLoader size={2} color="white" />}
					{!isSendingEmail && "Send"}
				</Button>
			</EmailPanel>
		);
	};

	renderHeader = () => {
		const ranges: Array<{ value: Range; label: string }> = [
			{ value: "day", label: "Today" },
			{ value: "week", label: "This Week" },
			{ value: "month", label: "This Month" },
			{ value: "financial-year", label: "This FY" },
			{ value: "last-financial-year", label: "Last FY" },
		];

		return (
			<Header>
				<HeaderText>
					<h1>Dashboard</h1>
					<p>Today&apos;s overview of your store</p>
				</HeaderText>
				<HeaderActions>
					<Tabs>
						{ranges.map(item => (
							<Tab
								key={item.value}
								active={this.state.range === item.value}
								onClick={() => this.setRange(item.value)}
							>
								{item.label}
							</Tab>
						))}
					</Tabs>
					<DropDown
						active={this.state.sendEmailActive}
						open={() => this.setState({ sendEmailActive: true })}
						close={() => this.setState({ sendEmailActive: false })}
						width={420}
						offset={15}
						flow="left"
						content={this.renderEmailSelect()}
					>
						<EmailButton type="button">
							<Icon compact src={`${ASSET_ROOT}/mail.svg`} alt="" />
							Email
						</EmailButton>
					</DropDown>
				</HeaderActions>
			</Header>
		);
	};

	renderMetrics = (data: T.API.DashboardReportBasic) => {
		const metrics = [
			{ label: "Total Sales", value: this.formatCurrency(data.sales_total), icon: "currency-dollar" },
			{ label: "Total Orders", value: String(data.orders_total), icon: "shopping-bag" },
			{ label: "Total Tips", value: this.formatCurrency(data.tips_total), icon: "piggy-bank" },
			{ label: "Number of Tips", value: String(data.tips_count), icon: "coins-hand" },
			{ label: "Total Reservations", value: String(data.reservations_total), icon: "tablet" },
			{ label: "Available Tables", value: String(data.available_tables), icon: "file-check" },
		];

		return (
			<Metrics>
				{metrics.map(metric => (
					<MetricCard key={metric.label}>
						<IconBox>
							<Icon compact src={`${ASSET_ROOT}/${metric.icon}.svg`} alt="" />
						</IconBox>
						<MetricText>
							<p>{metric.label}</p>
							<strong>{metric.value}</strong>
						</MetricText>
					</MetricCard>
				))}
			</Metrics>
		);
	};

	renderChart = (data: T.API.DashboardReportBasic) => {
		const { chart, chartType } = this.state;
		const isSales = chart === "sales";
		const totals = isSales ? data.sales_breakdown : data.orders_breakdown;
		const serviceBreakdown = data.service_breakdown || [];
		const isStacked = serviceBreakdown.length === totals.length && totals.length > 0;
		const chartData = totals.map((point, index) => {
			const services = serviceBreakdown[index];
			const values = services ? (isSales ? services.sales : services.counts) : null;
			return {
				name: point.l,
				value: point.v,
				delivery: values ? values.delivery : 0,
				pickup: values ? values.pickup : 0,
				dine_in: values ? values.dine_in : 0,
				table_booking: values ? values.table_booking : 0,
			};
		});
		const color = "#fb2f2f";
		const commonProps = {
			data: chartData,
			margin: { top: 20, right: 16, bottom: 12, left: 8 },
		};

		return (
			<ChartCard>
				<CardTop>
					<Tabs>
						<Tab active={chart === "sales"} onClick={() => this.setState({ chart: "sales" })}>
							Sales
						</Tab>
						<Tab active={chart === "orders"} onClick={() => this.setState({ chart: "orders" })}>
							Orders
						</Tab>
					</Tabs>
					<Tabs>
						<Tab active={chartType === "line"} onClick={() => this.setState({ chartType: "line" })}>
							Line
						</Tab>
						<Tab active={chartType === "bar"} onClick={() => this.setState({ chartType: "bar" })}>
							Bar
						</Tab>
					</Tabs>
				</CardTop>
				<ChartSummary>
					<strong>{isSales ? this.formatCurrency(data.sales_total) : data.orders_total}</strong>
					<p>{this.formatRangeTitle()}</p>
				</ChartSummary>
				<ChartArea>
					<ResponsiveContainer width="100%" height="100%">
						{chartType === "bar" ? (
							<BarChart {...commonProps}>
								<CartesianGrid stroke="#e9eaeb" vertical={false} />
								<XAxis dataKey="name" tick={{ fill: "#6b7270", fontSize: 12 }} />
								<YAxis tick={{ fill: "#6b7270", fontSize: 12 }} />
								<Tooltip
									formatter={(value: any) =>
										isSales ? this.formatCurrency(Number(value)) : String(value)
									}
								/>
								{isStacked ? (
									SERVICE_LEGEND.map((service, index) => (
										<Bar
											key={service.key}
											dataKey={service.key}
											name={service.label}
											stackId="services"
											fill={service.color}
											maxBarSize={32}
										>
											{index === SERVICE_LEGEND.length - 1 && (
												<LabelList
													className="chart-total-label"
													dataKey="value"
													position="top"
													formatter={(value: any) => formatCompact(Number(value))}
												/>
											)}
										</Bar>
									))
								) : (
									<Bar dataKey="value" name="Total" fill={color} maxBarSize={32} />
								)}
							</BarChart>
						) : (
							<LineChart {...commonProps}>
								<CartesianGrid stroke="#e9eaeb" vertical={false} />
								<XAxis dataKey="name" tick={{ fill: "#6b7270", fontSize: 12 }} />
								<YAxis tick={{ fill: "#6b7270", fontSize: 12 }} />
								<Tooltip
									formatter={(value: any) =>
										isSales ? this.formatCurrency(Number(value)) : String(value)
									}
								/>
								{isStacked ? (
									SERVICE_LEGEND.map(service => (
										<Line
											key={service.key}
											dataKey={service.key}
											name={service.label}
											stroke={service.color}
											strokeWidth={3}
											dot={false}
											type="monotone"
										/>
									))
								) : (
									<Line dataKey="value" name="Total" stroke={color} strokeWidth={3} type="monotone" />
								)}
							</LineChart>
						)}
					</ResponsiveContainer>
				</ChartArea>
				<ChartLegend>
						{SERVICE_LEGEND.map(item => {
							const value = isSales
								? this.formatCurrency(data.service_sales[item.key] || 0)
								: String(data.service_counts[item.key] || 0);
							return (
								<ChartLegendItem key={item.key}>
									<ChartLegendSwatch color={item.color} />
									<ChartLegendLabel>
										<span>{item.label} :</span>
										{` ${value}`}
									</ChartLegendLabel>
								</ChartLegendItem>
							);
						})}
					</ChartLegend>
			</ChartCard>
		);
	};

	getDetails = (data: T.API.DashboardReportBasic): DetailDefinition[] => {
		const store = this.injected.store;
		const restaurant = store.restaurant!;
		const paymentIcon = (key: string) => {
			const normalized = key.toLowerCase();
			if (normalized.includes("cash")) return "cash";
			if (normalized.includes("stripe")) return "stripe";
			if (normalized.includes("cardconnect")) return "cardconnect";
			if (normalized.includes("paypal")) return "paypal";
			if (normalized.includes("bambora")) return "bambora";
			return "card";
		};
		const paymentItems = Object.keys(data.payment_counts)
			.map(key => {
				const payment = restaurant.settings.payments[key];
				const value = data.payment_sales[key] || 0;
				if (!payment || (!payment.enabled && value === 0)) return null;
				return {
					name: store.getPaymentMethodName(key),
					value,
					amount: value,
					icon: paymentIcon(key),
				};
			})
			.filter(Boolean) as DetailItem[];

		const serviceItems = Object.keys(data.service_counts).map(key => {
			const serviceKey = key as T.Schema.Restaurant.Services.RestaurantServiceTypes;
			const serviceIcons = {
				dine_in: "dine-in",
				pickup: "takeaway",
				delivery: "delivery",
				table_booking: "reservations",
			};
			return {
				name: this.injected.t(`constants.services.${key}`),
				value: data.service_counts[serviceKey] || 0,
				amount: data.service_sales[serviceKey] || 0,
				icon: serviceIcons[serviceKey],
			};
		});

		const deliveryItems = data.delivery_stats.map(item => ({
			name: RestaurantUtils.settings.getDeliveryProviderName(item.provider || ""),
			value: item.totalSale,
			amount: item.totalSale,
			icon:
				String(item.provider).toLowerCase().includes("lalamove")
					? "lalamove"
					: String(item.provider).toLowerCase().includes("uber")
					? "uber"
					: String(item.provider).toLowerCase().includes("shipday")
					? "shipday"
					: "delivery",
		}));

		const statusItems = [
			{
				name: "Complete",
				value: data.status_sales.complete,
				amount: data.status_sales.complete,
				icon: "complete",
			},
			{
				name: "Incomplete",
				value: data.status_sales.incomplete,
				amount: data.status_sales.incomplete,
				icon: "incomplete",
			},
			{
				name: "Cancelled",
				value: data.status_sales.cancelled,
				amount: data.status_sales.cancelled,
				icon: "cancelled",
			},
		];

		return [
			{
				key: "payments",
				title: "Payment Methods",
				subtitle: "% of Total Sales",
				icon: "payment-methods",
				total: data.sales_total,
				items: paymentItems,
			},
			{
				key: "services",
				title: "Order Types",
				subtitle: "% of Total Orders",
				icon: "order-types",
				total: data.orders_total,
				items: serviceItems,
			},
			{
				key: "delivery",
				title: "Delivery Providers",
				subtitle: "% of Delivery Sales",
				icon: "delivery-providers",
				total: deliveryItems.reduce((sum, item) => sum + item.value, 0),
				items: deliveryItems,
			},
			{
				key: "status",
				title: "Order Completion",
				subtitle: "% of Total Sales",
				icon: "order-completion",
				total: data.sales_total,
				items: statusItems,
			},
		];
	};

	renderDetails = (data: T.API.DashboardReportBasic) => (
		<DetailGrid>
			{this.getDetails(data).map(detail => {
				const mode = this.state.detailModes[detail.key] || "percent";
				return (
					<DetailCard key={detail.key}>
						<DetailHeading>
							<IconBox small>
								<Icon compact src={`${ASSET_ROOT}/${detail.icon}.svg`} alt="" style={{ height: "auto" }} />
							</IconBox>
							<div>
								<h2>{detail.title}</h2>
								<p>{detail.subtitle}</p>
							</div>
							<CompactTabs>
								<Tab
									active={mode === "percent"}
									onClick={() =>
										this.setState({
											detailModes: { ...this.state.detailModes, [detail.key]: "percent" },
										})
									}
								>
									Percent
								</Tab>
								<Tab
									active={mode === "amount"}
									onClick={() =>
										this.setState({
											detailModes: { ...this.state.detailModes, [detail.key]: "amount" },
										})
									}
								>
									Amount
								</Tab>
							</CompactTabs>
							<ModeDropdown
								value={mode}
								onChange={e =>
									this.setState({
										detailModes: {
											...this.state.detailModes,
											[detail.key]: e.target.value as DetailMode,
										},
									})
								}
							>
								<option value="percent">Percent</option>
								<option value="amount">Amount</option>
							</ModeDropdown>
						</DetailHeading>
						{detail.items.length ? (
							detail.items.map(item => {
								const percent = detail.total ? (item.value / detail.total) * 100 : 0;
								return (
									<ProgressItem key={item.name}>
										<RowMarker>
											<img src={`${ASSET_ROOT}/${item.icon}.svg`} alt="" />
										</RowMarker>
										<ProgressContent>
											<ProgressLabel>{item.name}</ProgressLabel>
											<ProgressLine>
												<Track>
													<Fill width={percent} />
												</Track>
												<ProgressValue>
													{mode === "percent"
														? `${percent.toFixed(0)}%`
														: this.formatCurrency(item.amount)}
												</ProgressValue>
											</ProgressLine>
										</ProgressContent>
									</ProgressItem>
								);
							})
						) : (
							<Empty>No data for this period</Empty>
						)}
					</DetailCard>
				);
			})}
		</DetailGrid>
	);

	getCountRows = (data: T.API.DashboardReportBasic) => {
		if (this.state.counts === "category") {
			const restaurant = this.injected.store.restaurant!;
			return data.category_counts
				.map(item => {
					const category = RestaurantUtils.menu.findCategoryById(restaurant, item._id);
					return {
						name: category ? category.category.display_name || category.category.name : `Deleted Category`,
						qty: item.qty,
					};
				})
				.sort((a, b) => b.qty - a.qty);
		}
		return [...data.dish_counts].sort((a, b) => b.qty - a.qty);
	};

	renderCounts = (data: T.API.DashboardReportBasic) => {
		const rows = this.getCountRows(data);
		return (
			<CountCard>
				<CardTop>
					<CountTitle>Order Count</CountTitle>
					<Tabs>
						<Tab active={this.state.counts === "item"} onClick={() => this.setState({ counts: "item" })}>
							Item Count
						</Tab>
						<Tab
							active={this.state.counts === "category"}
							onClick={() => this.setState({ counts: "category" })}
						>
							Category Counts
						</Tab>
						<Tab active={this.state.counts === "tax"} onClick={() => this.setState({ counts: "tax" })}>
							Tax
						</Tab>
					</Tabs>
				</CardTop>
				{this.state.counts === "tax" ? (
					<Table>
						{data.tax_totals && data.tax_totals.length ? (
							data.tax_totals.map((item: any, index: number) => (
								<TableRow key={`${item.name}-${index}`}>
									<span>{item.name || "Tax"}</span>
									<span>{this.formatCurrency(Number(item.amount || 0))}</span>
								</TableRow>
							))
						) : (
							<Empty>No tax data for this period</Empty>
						)}
					</Table>
				) : (
					<Table>
						<TableRow heading>
							<span>{this.state.counts === "category" ? "Category" : "Items"}</span>
							<span>Count</span>
						</TableRow>
						{rows.length ? (
							rows.map((item, index) => (
								<TableRow key={`${item.name}-${index}`}>
									<span>{item.name}</span>
									<span>{item.qty}</span>
								</TableRow>
							))
						) : (
							<Empty>No count data for this period</Empty>
						)}
					</Table>
				)}
			</CountCard>
		);
	};

	render() {
		const { data, error } = this.state;
		return (
			<Page>
				{this.renderHeader()}
				{!data && !error && (
					<Empty>
						<RotateLoader />
					</Empty>
				)}
				{error && (
					<ErrorPanel>
						<p>Something went wrong loading your dashboard.</p>
						<button onClick={this.query}>Try Again</button>
					</ErrorPanel>
				)}
				{data && data.outcome === 0 && (
					<>
						{this.renderMetrics(data)}
						{this.renderChart(data)}
						{this.renderDetails(data)}
						{this.renderCounts(data)}
					</>
				)}
			</Page>
		);
	}
}

// @ts-ignore
export const RestaurantDashboardV2 = withTheme(withTranslation()(RestaurantDashboardV2Class));
